import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { ChevronDown, ScrollText, Flame, ArrowLeft, ArrowRight, BookOpenCheck } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "./ui/dropdown-menu";

// Long Arabic statute kinds and a shorter, friendlier label for the menu.
const KIND_LABELS_AR = {
  "مرسوم": { ar: "مراسيم", en: "Decrees" },
  "قانون": { ar: "قوانين", en: "Laws" },
  "قرار": { ar: "قرارات", en: "Resolutions" },
  "اتفاقية / معاهدة": { ar: "اتفاقيات", en: "Treaties" },
  "لائحة / نظام": { ar: "لوائح", en: "Regulations" },
  "دستور": { ar: "الدستور", en: "Constitution" },
  "مرسوم بقانون": { ar: "مراسيم بقانون", en: "Decree-Laws" },
  "نموذج / إقرار / تعهد": { ar: "نماذج", en: "Forms" },
  "مستند / صفحة حكومية": { ar: "مستندات حكومية", en: "Gov Docs" },
  "تعميم": { ar: "تعاميم", en: "Circulars" },
};

/**
 * Mega-dropdown for the "القوانين / Laws" nav item.
 *
 * The trigger acts like a normal NavLink (clicking the label goes to
 * /laws), but hovering exposes a categorized panel with statute kinds,
 * a top-trending list, and quick-action footers.
 */
export default function LawsNavMenu() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    api.get("/laws").then((r) => {
      const rows = r.data || [];
      const isReg = (l) =>
        l.document_kind === "regulation" || l.doc_type === "regulation" ||
        /^\s*(اللائحة|لائحة|اللائحه|لائحه|القرار|قرار)/.test((l.title || "").trim());
      const isConst = (l) => (l.category || "") === "دستوري";
      let laws = 0, regs = 0, consts = 0;
      for (const l of rows) {
        if (isConst(l)) consts++;
        else if (isReg(l)) regs++;
        else laws++;
      }
      setStats({ total: rows.length, laws, regs, consts });
    }).catch(() => {});
    api.get("/laws/popular", { params: { limit: 4 } })
      .then((r) => setPopular(r.data || []))
      .catch(() => {});
  }, []);

  const t = useMemo(() => ({
    label: isAr ? "المكتبة القانونية" : "Legal Library",
    eyebrow: isAr ? "مكتبة قانونية" : "Library",
    title: isAr ? "تصفّح حسب النوع" : "Browse by kind",
    all: isAr ? "كل القوانين" : "All laws",
    trending: isAr ? "الأكثر تنزيلاً" : "Trending now",
    viewAll: isAr ? "عرض الكل ←" : "View all →",
    totalLabel: (n) => isAr ? `${n} وثيقة منشورة` : `${n} public documents`,
  }), [isAr]);

  const orderedKinds = useMemo(() => {
    if (!stats) return [];
    return [
      { key: "law", ar: "القوانين", en: "Laws", count: stats.laws, to: "/laws?type=law" },
      { key: "regulation", ar: "اللوائح والقرارات", en: "Regulations", count: stats.regs, to: "/laws?type=regulation" },
      { key: "constitution", ar: "الدستور", en: "Constitution", count: stats.consts, to: "/laws?category=دستوري" },
    ].filter((b) => b.count > 0);
  }, [stats]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {/* Wrap so hover-out still keeps menu briefly via the menu's
          own positioning; trigger remains a real link for accessibility. */}
      <div
        className="relative inline-flex"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <DropdownMenuTrigger asChild>
          <NavLink
            to="/laws"
            data-testid="nav-link-laws"
            className={({ isActive }) =>
              `inline-flex items-center gap-1 text-sm whitespace-nowrap transition-colors ${
                isActive ? "font-semibold" : "hover:opacity-80"
              }`
            }
            style={({ isActive }) => ({
              color: isActive ? "var(--gold-deep)" : "var(--ink)",
            })}
          >
            {t.label}
            <ChevronDown className="w-3.5 h-3.5 opacity-60" />
          </NavLink>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="center"
          sideOffset={14}
          className="rounded-sm p-0 overflow-hidden shadow-2xl"
          style={{
            background: "var(--bg)",
            border: "1px solid var(--line)",
            width: "min(720px, 92vw)",
          }}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="grid grid-cols-1 md:grid-cols-3">
            {/* Column 1+2 — categories */}
            <div
              className="md:col-span-2 p-6 lg:p-7"
              style={{ borderInlineEnd: "1px solid var(--line)" }}
            >
              <p className="eyebrow inline-flex items-center gap-1.5 mb-1">
                <BookOpenCheck
                  className="w-3.5 h-3.5"
                  style={{ color: "var(--gold-deep)" }}
                />
                {t.eyebrow}
              </p>
              <h3 className="font-display text-xl tracking-tight">{t.title}</h3>
              {stats && (
                <p className="text-xs text-slate-500 mt-1">{t.totalLabel(stats.total)}</p>
              )}

              <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-5">
                {orderedKinds.map((b) => (
                    <Link
                      key={b.key}
                      to={b.to}
                      onClick={() => setOpen(false)}
                      data-testid={`laws-menu-kind-${b.key}`}
                      className="group flex items-center justify-between gap-3 py-2 text-sm hover:text-[var(--gold-deep)] transition-colors"
                    >
                      <span className="inline-flex items-center gap-2">
                        <ScrollText
                          className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity"
                        />
                        {isAr ? b.ar : b.en}
                      </span>
                      <span
                        className="text-[11px] tabular-nums px-1.5 rounded-sm"
                        style={{ background: "var(--beige)", color: "var(--gold-deep)" }}
                      >
                        {b.count}
                      </span>
                    </Link>
                ))}
              </div>

              <Link
                to="/laws"
                onClick={() => setOpen(false)}
                data-testid="laws-menu-view-all"
                className="inline-flex items-center gap-1.5 mt-6 text-sm font-medium hover:opacity-80"
                style={{ color: "var(--gold-deep)" }}
              >
                {t.all}
                {isAr ? <ArrowLeft className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
              </Link>
            </div>

            {/* Column 3 — trending */}
            <div className="p-6 lg:p-7" style={{ background: "var(--paper)" }}>
              <p className="eyebrow inline-flex items-center gap-1.5 mb-3">
                <Flame
                  className="w-3.5 h-3.5"
                  style={{ color: "var(--gold-deep)" }}
                />
                {t.trending}
              </p>
              <ul className="space-y-3">
                {popular.length === 0 ? (
                  <li className="text-xs text-slate-500">
                    {isAr ? "لا توجد إحصاءات بعد." : "No stats yet."}
                  </li>
                ) : (
                  popular.slice(0, 4).map((p, i) => (
                    <li key={p.id}>
                      <Link
                        to={`/laws/${p.id}`}
                        onClick={() => setOpen(false)}
                        data-testid={`laws-menu-popular-${p.id}`}
                        className="flex items-start gap-2.5 text-sm leading-snug hover:text-[var(--gold-deep)] transition-colors"
                      >
                        <span
                          className="shrink-0 font-display text-base leading-none mt-0.5 w-5"
                          style={{ color: "var(--gold-deep)" }}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span dir="rtl" className="line-clamp-2">{p.title}</span>
                      </Link>
                    </li>
                  ))
                )}
              </ul>
              <Link
                to="/laws"
                onClick={() => setOpen(false)}
                className="inline-block mt-6 text-xs uppercase tracking-[0.2em] hover:opacity-80"
                style={{ color: "var(--gold-deep)" }}
              >
                {t.viewAll}
              </Link>
            </div>
          </div>
        </DropdownMenuContent>
      </div>
    </DropdownMenu>
  );
}
